import * as THREE from "three";
import { InstanceBatch } from "./instance_batch.js";
import { ResourceID } from "../../../resource_loader/resource_id.js";
import { Vfx3DGlobalAtlas } from "../textures/vfx_3d_global_atlas.js";
import { AttributeSize } from "../types/core_types.js";

export const ShockwaveLayout = {
  aPosTime:    AttributeSize.Vec4, // x, y, z, start_time
  aParams:     AttributeSize.Vec3, // max_scale, duration, deceleration_power
  aFadeParams: AttributeSize.Vec2, // fade_start, max_alpha
  aUvBounds:   AttributeSize.Vec4, // min_x, min_y, max_x, max_y
} as const;

export class VfxShockwaveBatch extends InstanceBatch<typeof ShockwaveLayout>
{
  private static readonly MAX_INSTANCES : number = 20;
  private static readonly RENDER_ORDER  : number = 20;

  private static cached_geometry : THREE.BufferGeometry | null = null;
  private texture_initialized    : boolean = false;

  public constructor()
  {
    const base_geometry = VfxShockwaveBatch.getGeometry();

    let atlas_texture: THREE.Texture | null = null;
    try { atlas_texture = Vfx3DGlobalAtlas.getTexture(); } catch ( _e ) {}

    const material = new THREE.ShaderMaterial( {
      uniforms: {
        uTime:    { value: 0.0 },
        uTexture: { value: atlas_texture },
      },
      vertexShader: `
        uniform float uTime;

        attribute vec4 aPosTime;
        attribute vec3 aParams;     // x: max_scale, y: duration, z: deceleration_power
        attribute vec2 aFadeParams; // x: fade_start, y: max_alpha
        attribute vec4 aUvBounds;

        varying vec2 vUv;
        varying float vAlpha;

        void main() {
          float start_time = aPosTime.w;
          float duration = aParams.y;
          float age = max( 0.0, uTime - start_time );
          float progress = duration > 0.0 ? clamp( age / duration, 0.0, 1.0 ) : 0.0;

          vUv = mix( aUvBounds.xy, aUvBounds.zw, uv );

          // Fade out starts at fade_start (0.0..1.0)
          float fade_start = aFadeParams.x;
          float max_alpha = aFadeParams.y;
          float fade = 1.0 - clamp( ( progress - fade_start ) / max( 1.0 - fade_start, 0.0001 ), 0.0, 1.0 );
          vAlpha = fade * max_alpha;

          // Ease-out expansion curve
          float max_scale = aParams.x;
          float decel_power = aParams.z;
          float ease_dist = 1.0 - pow( 1.0 - progress, decel_power );
          float current_scale = max_scale * ease_dist;

          // Billboard facing camera in view space
          vec4 mv_position = modelViewMatrix * vec4( aPosTime.xyz, 1.0 );
          mv_position.xy += position.xy * current_scale;
          gl_Position = projectionMatrix * mv_position;
        }
      `,
      fragmentShader: `
        uniform sampler2D uTexture;

        varying vec2 vUv;
        varying float vAlpha;

        void main() {
          vec4 glow_sample = texture2D( uTexture, vUv );
          float final_alpha = glow_sample.a * vAlpha;
          gl_FragColor = vec4( vec3( 1.0 ), final_alpha );
        }
      `,
      transparent: true,
      depthWrite: false,
      depthTest: true,
      blending: THREE.AdditiveBlending,
      side: THREE.FrontSide,
    } );

    super( { max_instances: VfxShockwaveBatch.MAX_INSTANCES, layout: ShockwaveLayout }, base_geometry, material );
    this.instanced_mesh.renderOrder = VfxShockwaveBatch.RENDER_ORDER;
  }

  private static getGeometry(): THREE.BufferGeometry
  {
    if ( VfxShockwaveBatch.cached_geometry )
      return VfxShockwaveBatch.cached_geometry;

    const geom = new THREE.PlaneGeometry( 1.0, 1.0 );
    VfxShockwaveBatch.cached_geometry = geom;
    return geom;
  }

  public spawnParticle(
    res_id: ResourceID,
    start_pos: THREE.Vector3,
    max_scale: number,
    duration: number,
    start_time: number,
    deceleration_power: number = 3.0,
    max_alpha: number = 1.0,
    fade_start_progress: number = 0.0
  ): number
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( !this.texture_initialized || !mat.uniforms.uTexture.value )
    {
      try
      {
        mat.uniforms.uTexture.value = Vfx3DGlobalAtlas.getTexture();
        this.texture_initialized = true;
      }
      catch ( _e ) {}
    }

    const glow_uv = Vfx3DGlobalAtlas.getUVRect( res_id );

    const idx = this.allocateInstanceIndex( start_time, duration );
    if ( idx === -1 )
      return -1;

    const pos_time_attr    = this.geometry.getAttribute( "aPosTime" ).array as Float32Array;
    const params_attr      = this.geometry.getAttribute( "aParams" ).array as Float32Array;
    const fade_params_attr = this.geometry.getAttribute( "aFadeParams" ).array as Float32Array;
    const uv_bounds_attr   = this.geometry.getAttribute( "aUvBounds" ).array as Float32Array;

    const o4 = idx * 4;
    pos_time_attr[ o4 ]     = start_pos.x;
    pos_time_attr[ o4 + 1 ] = start_pos.y;
    pos_time_attr[ o4 + 2 ] = start_pos.z;
    pos_time_attr[ o4 + 3 ] = start_time;

    params_attr[ idx * 3 ]     = max_scale;
    params_attr[ idx * 3 + 1 ] = duration;
    params_attr[ idx * 3 + 2 ] = deceleration_power;

    fade_params_attr[ idx * 2 ]     = fade_start_progress;
    fade_params_attr[ idx * 2 + 1 ] = max_alpha;

    if ( glow_uv )
    {
      uv_bounds_attr[ o4 ]     = glow_uv.min_x;
      uv_bounds_attr[ o4 + 1 ] = glow_uv.min_y;
      uv_bounds_attr[ o4 + 2 ] = glow_uv.max_x;
      uv_bounds_attr[ o4 + 3 ] = glow_uv.max_y;
    }

    this.geometry.getAttribute( "aPosTime" ).needsUpdate    = true;
    this.geometry.getAttribute( "aParams" ).needsUpdate     = true;
    this.geometry.getAttribute( "aFadeParams" ).needsUpdate = true;
    this.geometry.getAttribute( "aUvBounds" ).needsUpdate   = true;

    return this.getStableId( idx );
  }


  protected override onUpdate( _dt: number, now: number ): void
  {
    const mat = this.material as THREE.ShaderMaterial;
    if ( mat.uniforms.uTime )
    {
      mat.uniforms.uTime.value = now;
    }
  }

  protected override updateInstance( _progress: number, _now: number, _idx: number ): boolean
  {
    return true;
  }
}
